<template>
  <Card class="slot-card">
    <CardHeader class="slot-header">
      <Badge :variant="getSlotState() === 0 ? 'secondary' : 'outline'">{{ connSlot.type }}</Badge>
      <span v-if="connSlot.product" class="slot-product">{{ connSlot.product }}</span>
      <Button
        size="sm"
        variant="outline"
        :disabled="loading"
        class="ml-auto"
        @click="changeSlotState()"
      >
        <Spinner v-if="loading" />
        <PlugZap v-else :size="14" />
        {{ connectionStateToTakeSlotButtonText(getSlotState()) }}
      </Button>
    </CardHeader>
    <CardContent class="space-y-3">
      <div class="slot-info">
        <div class="info-row">
          <Server :size="14" class="info-icon" />
          <span>{{ connSlot.server_address }}</span>
        </div>
        <div class="info-row">
          <UserRound :size="14" class="info-icon" />
          <span>{{ connSlot.account }}</span>
        </div>
      </div>
      <div v-if="connSlot.connection" class="connection-user">
        <div class="staff-avatar">{{ getUserInitials() }}</div>
        <div class="min-w-0">
          <div class="text-sm font-medium">{{ connSlot.connection.user.username }}</div>
          <div class="text-xs text-muted-foreground mt-1">
            {{ formatDateTime(connSlot.connection.date_started) }}
          </div>
        </div>
      </div>
    </CardContent>
    <connection-queue :queue="slotQueue()" />
  </Card>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PlugZap, Server, UserRound } from '@lucide/vue';

import { ref } from 'vue';
import axios from 'axios';
import moment from 'moment';
import { ConnectionSlotState } from '../models/saas_extension';
import ConnectionQueue from './ConnectionQueue.vue';
import { useMainStore } from '@/store';

interface Props {
  connSlot: Slot;
  queue: Queue[];
}

const props = defineProps<Props>();
const emit = defineEmits(['slotChanged']);
const store = useMainStore();
const loading = ref(false);

function connectionStateToTakeSlotButtonText(value: ConnectionSlotState): string {
  switch (value) {
    case ConnectionSlotState.Ready:
      return 'Занять';
    case ConnectionSlotState.BusyByAnotherUser:
      return 'В очередь';
    case ConnectionSlotState.BusyByCurrentUser:
      return 'Освободить';
    default:
      return '';
  }
}

function slotQueue() {
  const filtered = props.queue
    .filter(
      (q) =>
        q.property === props.connSlot.property_id &&
        (q.product === null || q.product == props.connSlot.product_id)
    )
    .sort((x, y) => (x.date_created > y.date_created ? 1 : 0));
  return filtered;
}

function getSlotState(): ConnectionSlotState {
  if (props.connSlot.connection != null) {
    if (props.connSlot.connection.user.username == store.currentUserName) {
      return ConnectionSlotState.BusyByCurrentUser;
    }
    return ConnectionSlotState.BusyByAnotherUser;
  } else {
    return ConnectionSlotState.Ready;
  }
}

function getUserInitials(): string {
  if (!props.connSlot.connection) return '';
  const username = props.connSlot.connection.user.username;
  return username.substring(0, 2).toUpperCase();
}

function formatDateTime(dateTime: string): string {
  return moment(dateTime).format('DD.MM.YYYY HH:mm');
}

async function changeSlotState() {
  loading.value = true;
  const state = getSlotState();
  switch (state) {
    case ConnectionSlotState.Ready:
    case ConnectionSlotState.BusyByAnotherUser:
      await axios.post('/api/slots/' + props.connSlot.id + '/take/').then(
        (response) => {
          if (response.status == 201 && state == ConnectionSlotState.Ready) {
            emit('slotChanged');
          }
          loading.value = false;
        },
        (_error) => {
          loading.value = false;
        }
      );
      break;
    case ConnectionSlotState.BusyByCurrentUser:
      await axios.post('/api/slots/' + props.connSlot.id + '/release/').then(
        (response) => {
          if (response.status == 201) {
            emit('slotChanged');
          }
          loading.value = false;
        },
        (_error) => {
          loading.value = false;
        }
      );
      break;
  }
}
</script>
